import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;
const SEARCH_STORAGE_KEY = "travel_search";
const DISTANCE_BATCH_SIZE = 5;
const NEARBY_RADIUS_KM = 50;

let googleMapsPromise = null;
const distanceCache = new Map();

function waitForGoogleMaps(timeoutMs = 15000, intervalMs = 100) {
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const timer = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(timer);
        googleMapsPromise = null;
        resolve(window.google.maps);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        googleMapsPromise = null;
        reject(
          new Error(
            "Google Maps API não ficou disponível a tempo. Verifique se ela está sendo carregada uma única vez na aplicação.",
          ),
        );
      }
    }, intervalMs);
  });

  return googleMapsPromise;
}

async function getGoogleMaps() {
  return waitForGoogleMaps();
}

async function processInBatches(items, batchSize, mapper) {
  const results = [];

  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const batchResults = await Promise.all(batch.map(mapper));
    results.push(...batchResults);
  }

  return results;
}

function getDistanceCacheKey(origem, destino) {
  return [
    Number(origem.lat).toFixed(6),
    Number(origem.lng).toFixed(6),
    Number(destino.lat).toFixed(6),
    Number(destino.lng).toFixed(6),
  ].join("|");
}

async function calcularDistanciaKm(maps, origem, destino) {
  const key = getDistanceCacheKey(origem, destino);

  if (distanceCache.has(key)) {
    return distanceCache.get(key);
  }

  const promise = new Promise((resolve) => {
    try {
      const service = new maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [new maps.LatLng(origem.lat, origem.lng)],
          destinations: [new maps.LatLng(destino.lat, destino.lng)],
          travelMode: maps.TravelMode.DRIVING,
          unitSystem: maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status !== "OK") {
            console.warn("DistanceMatrix status:", status, { origem, destino });
            resolve(Number.POSITIVE_INFINITY);
            return;
          }

          const element = response?.rows?.[0]?.elements?.[0];

          if (!element || !element.distance) {
            resolve(Number.POSITIVE_INFINITY);
            return;
          }

          if (element.status && element.status !== "OK") {
            console.warn("DistanceMatrix element status:", element.status, {
              origem,
              destino,
            });
            resolve(Number.POSITIVE_INFINITY);
            return;
          }

          const km = Math.round(element.distance.value / 1000);
          resolve(Number.isFinite(km) ? km : Number.POSITIVE_INFINITY);
        },
      );
    } catch (err) {
      console.error("Erro ao chamar DistanceMatrixService:", err);
      resolve(Number.POSITIVE_INFINITY);
    }
  });

  distanceCache.set(key, promise);
  return promise;
}

function extractCityName(value) {
  return String(value ?? "")
    .split(",")[0]
    .trim()
    .toLowerCase();
}

function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function parsePriceToNumber(valor) {
  if (valor === null || valor === undefined || valor === "") {
    return Number.POSITIVE_INFINITY;
  }

  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : Number.POSITIVE_INFINITY;
  }

  const cleaned = String(valor)
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function formatPrice(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "Preço indisponível";
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function parseDurationToMinutes(valor) {
  if (valor === null || valor === undefined || valor === "") {
    return Number.POSITIVE_INFINITY;
  }

  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : Number.POSITIVE_INFINITY;
  }

  const text = normalizeText(valor);

  // Ex.: "10:30"
  const hhmm = text.match(/^(\d+):(\d{2})$/);
  if (hhmm) {
    return Number(hhmm[1]) * 60 + Number(hhmm[2]);
  }

  let total = 0;
  let found = false;

  const daysMatch = text.match(/(\d+)\s*(d|dia|dias)\b/);
  if (daysMatch) {
    total += Number(daysMatch[1]) * 24 * 60;
    found = true;
  }

  const hoursMatch = text.match(/(\d+)\s*(h|hora|horas)\b/);
  if (hoursMatch) {
    total += Number(hoursMatch[1]) * 60;
    found = true;
  }

  const minutesMatch = text.match(/(\d+)\s*(m|min|minuto|minutos)\b/);
  if (minutesMatch) {
    total += Number(minutesMatch[1]);
    found = true;
  }

  if (found) return total;

  const numericOnly = Number(text.replace(/[^\d]/g, ""));
  if (Number.isFinite(numericOnly) && numericOnly > 0) {
    return numericOnly;
  }

  return Number.POSITIVE_INFINITY;
}

function getReferencePoint(searchData, byDestination = false) {
  const lat = Number(
    byDestination ? searchData.destino_lat : searchData.origem_lat,
  );
  const lng = Number(
    byDestination ? searchData.destino_lng : searchData.origem_lng,
  );
  const city = extractCityName(
    byDestination ? searchData.destino : searchData.origem,
  );

  return {
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    city,
  };
}

function isExactRouteMatch(trip, searchData) {
  const origemBusca = extractCityName(searchData.origem);
  const destinoBusca = extractCityName(searchData.destino);

  const origemTrip = extractCityName(trip.cidade_de_onde_vai_sair);
  const destinoTrip = extractCityName(trip.cidade_para_onde_vai);

  return origemTrip === origemBusca && destinoTrip === destinoBusca;
}

async function calcularDistanciaReferencia(trips, searchData, byDestination) {
  const maps = await getGoogleMaps();
  const ref = getReferencePoint(searchData, byDestination);

  return processInBatches(trips, DISTANCE_BATCH_SIZE, async (trip) => {
    const lat = Number(
      byDestination ? trip.lat_cidade_destino : trip.lat_cidade_origem,
    );
    const lng = Number(
      byDestination ? trip.lng_cidade_destino : trip.lng_cidade_origem,
    );

    if (
      ref.lat === null ||
      ref.lng === null ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      return {
        ...trip,
        distancia_ref_km: Number.POSITIVE_INFINITY,
      };
    }

    const km = await calcularDistanciaKm(
      maps,
      { lat: ref.lat, lng: ref.lng },
      { lat, lng },
    );

    return {
      ...trip,
      distancia_ref_km: Number.isFinite(km) ? km : Number.POSITIVE_INFINITY,
    };
  });
}

async function enriquecerViagens(trips) {
  try {
    const maps = await getGoogleMaps();

    const enriquecidas = await processInBatches(
      trips,
      DISTANCE_BATCH_SIZE,
      async (trip) => {
        const origem = {
          lat: Number(trip.lat_cidade_origem),
          lng: Number(trip.lng_cidade_origem),
        };

        const destino = {
          lat: Number(trip.lat_cidade_destino),
          lng: Number(trip.lng_cidade_destino),
        };

        let distanciaGoogleKm = null;

        if (
          Number.isFinite(origem.lat) &&
          Number.isFinite(origem.lng) &&
          Number.isFinite(destino.lat) &&
          Number.isFinite(destino.lng)
        ) {
          const distanciaKm = await calcularDistanciaKm(maps, origem, destino);
          distanciaGoogleKm = Number.isFinite(distanciaKm) ? distanciaKm : null;
        }

        const precoNumerico = parsePriceToNumber(trip.preco_estimado);

        return {
          ...trip,
          distancia_google_km: distanciaGoogleKm,
          preco_estimado_num: Number.isFinite(precoNumerico)
            ? precoNumerico
            : null,
          preco_estimado_formatado: Number.isFinite(precoNumerico)
            ? formatPrice(precoNumerico)
            : "Preço indisponível",
        };
      },
    );

    return enriquecidas;
  } catch (err) {
    console.error("Falha ao enriquecer viagens:", err);

    return trips.map((trip) => {
      const precoNumerico = parsePriceToNumber(trip.preco_estimado);

      return {
        ...trip,
        distancia_google_km: null,
        preco_estimado_num: Number.isFinite(precoNumerico)
          ? precoNumerico
          : null,
        preco_estimado_formatado: Number.isFinite(precoNumerico)
          ? formatPrice(precoNumerico)
          : "Preço indisponível",
      };
    });
  }
}

async function sortTripsByRecommendedMode(trips, mode) {
  if (mode === "cheapest") {
    return [...trips]
      .map((trip, index) => ({
        trip,
        index,
        price: parsePriceToNumber(
          trip.preco_estimado_num ?? trip.preco_estimado,
        ),
      }))
      .sort((a, b) => {
        if (a.price !== b.price) return a.price - b.price;
        return a.index - b.index;
      })
      .map((item) => item.trip);
  }

  // fastest
  return [...trips]
    .map((trip, index) => ({
      trip,
      index,
      duration: parseDurationToMinutes(trip.duracao_viagem),
      distance:
        typeof trip.distancia_google_km === "number"
          ? trip.distancia_google_km
          : Number.POSITIVE_INFINITY,
      price: parsePriceToNumber(trip.preco_estimado_num ?? trip.preco_estimado),
    }))
    .sort((a, b) => {
      if (a.duration !== b.duration) return a.duration - b.duration;
      if (a.distance !== b.distance) return a.distance - b.distance;
      if (a.price !== b.price) return a.price - b.price;
      return a.index - b.index;
    })
    .map((item) => item.trip);
}

async function sortTripsBySearchReference(
  trips,
  searchData,
  byDestination = false,
) {
  const ref = getReferencePoint(searchData, byDestination);

  const field = byDestination
    ? "cidade_para_onde_vai"
    : "cidade_de_onde_vai_sair";

  const enriched = await calcularDistanciaReferencia(
    trips,
    searchData,
    byDestination,
  );

  return enriched
    .map((trip, index) => {
      const origemBusca = extractCityName(searchData.origem);
      const destinoBusca = extractCityName(searchData.destino);

      const origemTrip = extractCityName(trip.cidade_de_onde_vai_sair);
      const destinoTrip = extractCityName(trip.cidade_para_onde_vai);

      const sameOrigin = origemTrip === origemBusca;
      const sameDestination = destinoTrip === destinoBusca;

      const tripCity = extractCityName(trip[field]);
      const exactCityMatch = ref.city && tripCity === ref.city ? 0 : 1;
      const nearby = trip.distancia_ref_km <= NEARBY_RADIUS_KM;

      let locationPriority = 3;

      if (!byDestination) {
        // cityMode = false -> foco na ORIGEM
        // 0 = mesma origem, destino diferente
        // 1 = origem próxima
        // 2 = mesmo destino, origem diferente
        // 3 = resto
        if (sameOrigin && !sameDestination) {
          locationPriority = 0;
        } else if (exactCityMatch === 0 || nearby) {
          locationPriority = 1;
        } else if (!sameOrigin && sameDestination) {
          locationPriority = 2;
        }
      } else {
        // cityMode = true -> foco no DESTINO
        // 0 = mesmo destino, origem diferente
        // 1 = destino próximo
        // 2 = mesma origem, destino diferente
        // 3 = resto
        if (sameDestination && !sameOrigin) {
          locationPriority = 0;
        } else if (exactCityMatch === 0 || nearby) {
          locationPriority = 1;
        } else if (sameOrigin && !sameDestination) {
          locationPriority = 2;
        }
      }

      return {
        trip,
        index,
        locationPriority,
        distance: trip.distancia_ref_km,
        price: parsePriceToNumber(
          trip.preco_estimado_num ?? trip.preco_estimado,
        ),
      };
    })
    .sort((a, b) => {
      if (a.locationPriority !== b.locationPriority) {
        return a.locationPriority - b.locationPriority;
      }

      if (a.distance !== b.distance) {
        return a.distance - b.distance;
      }

      if (a.price !== b.price) {
        return a.price - b.price;
      }

      return a.index - b.index;
    })
    .map((item) => item.trip);
}

export default function useBuscaViagens() {
  const location = useLocation();

  const searchData = useMemo(() => {
    const fromState = location.state;

    if (
      fromState?.origem &&
      fromState?.destino &&
      fromState?.encomenda &&
      fromState?.seguro
    ) {
      return fromState;
    }

    try {
      const saved = sessionStorage.getItem(SEARCH_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}

    return {
      origem: "",
      destino: "",
      encomenda: "",
      seguro: "",
      origem_lat: "",
      origem_lng: "",
      destino_lat: "",
      destino_lng: "",
    };
  }, [location.state]);

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [recommendedSort, setRecommendedSort] = useState("fastest");
  const [recommendedTrips, setRecommendedTrips] = useState([]);
  const [cityMode, setCityMode] = useState(false);
  const [otherTrips, setOtherTrips] = useState([]);

  useEffect(() => {
    let active = true;

    const buscar = async () => {
      const { origem, destino, encomenda, seguro } = searchData;

      if (!origem || !destino || !encomenda || !seguro) {
        if (active) {
          setTrips([]);
          setRecommendedTrips([]);
          setOtherTrips([]);
        }
        return;
      }

      try {
        setLoading(true);
        setError("");

        const url = new URL(`${API_URL}/api/viagens/buscar/`);
        url.searchParams.set("origem", origem);
        url.searchParams.set("destino", destino);
        url.searchParams.set("encomenda", encomenda);
        url.searchParams.set("seguro", seguro);

        if (searchData.origem_lat && searchData.origem_lng) {
          url.searchParams.set("origem_lat", searchData.origem_lat);
          url.searchParams.set("origem_lng", searchData.origem_lng);
        }

        if (searchData.destino_lat && searchData.destino_lng) {
          url.searchParams.set("destino_lat", searchData.destino_lat);
          url.searchParams.set("destino_lng", searchData.destino_lng);
        }

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.detail || "Erro ao buscar viagens.");
        }

        const result = Array.isArray(data.resultados) ? data.resultados : [];
        const enriched = await enriquecerViagens(result);

        if (!active) return;
        setTrips(enriched);
      } catch (err) {
        if (!active) return;
        setTrips([]);
        setRecommendedTrips([]);
        setOtherTrips([]);
        setError(err.message || "Erro inesperado.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    buscar();

    return () => {
      active = false;
    };
  }, [searchData]);

  useEffect(() => {
    let active = true;

    const ordenar = async () => {
      const base = trips.filter((trip) => isExactRouteMatch(trip, searchData));
      const sorted = await sortTripsByRecommendedMode(base, recommendedSort);

      if (active) {
        setRecommendedTrips(sorted);
      }
    };

    ordenar();

    return () => {
      active = false;
    };
  }, [trips, recommendedSort, searchData]);

  useEffect(() => {
    let active = true;

    const ordenar = async () => {
      const base = trips.filter((trip) => !isExactRouteMatch(trip, searchData));

      const sorted = await sortTripsBySearchReference(
        base,
        searchData,
        cityMode,
      );

      if (active) {
        setOtherTrips(sorted);
      }
    };

    ordenar();

    return () => {
      active = false;
    };
  }, [trips, cityMode, searchData]);

  return {
    searchData,
    trips,
    recommendedTrips,
    otherTrips,
    loading,
    error,
    recommendedSort,
    setRecommendedSort,
    cityMode,
    setCityMode,
  };
}
